/**
 * Component Registry
 *
 * This file serves as the single source of truth for all components displayed in the playground.
 *
 * To add a new component:
 * 1. Import the component at the top of this file
 * 2. Add an entry to the appropriate category/group in the registry below
 * 3. That's it! The component will automatically appear in the playground
 */

import { createElement } from "react"
import type { ComponentType } from "react"

// === Application Components ===
import { MessageActionAdvanced } from "@/components/untitled-ui/application/messaging/message-action-advanced"
import { MessageActionMinimal } from "@/components/untitled-ui/application/messaging/message-action-minimal"
import { MessageActionTextarea } from "@/components/untitled-ui/application/messaging/message-action-textarea"
import {
  MessageItem,
  MessageStatus,
} from "@/components/untitled-ui/application/messaging/messaging"
// === Derived Components ===
import { CoachingPrompts } from "@/components/untitled-ui/derived/CoachingPrompts"
import { SimulationMessageAction } from "@/components/untitled-ui/derived/SimulationMessageAction"

// === Playground Utilities ===
import { AIAssistantModalPlayground } from "./modal-wrapper"

// === Types ===
export type ComponentCategory = "application" | "marketing" | "derived"

export interface ComponentRegistryEntry {
  id: string
  name: string
  component: ComponentType<Record<string, unknown>>
  category: ComponentCategory
  group: string
  description?: string
  demoProps?: Record<string, unknown>
}

/**
 * Bind a component to its demo props behind a single erased type.
 *
 * Registry entries hold heterogeneous components with different prop shapes, so
 * they can't share one concrete `ComponentType`. `demo` captures each
 * component's props in a wrapper that renders it, keeping every call site fully
 * type-checked without resorting to `any`.
 */
function demo<P extends object>(
  Component: ComponentType<P>,
  demoProps: P,
): Pick<ComponentRegistryEntry, "component" | "demoProps"> {
  const Bound: ComponentType<Record<string, unknown>> = () =>
    createElement(Component, demoProps)
  return { component: Bound, demoProps: {} }
}

// === Registry ===
export const componentRegistry: ComponentRegistryEntry[] = [
  // ===== DERIVED =====
  {
    id: "derived-simulation-message-action",
    name: "Simulation Message Action",
    category: "derived",
    group: "messaging",
    description:
      "Customized message input for simulation chat with 'Help me word this' button",
    ...demo(SimulationMessageAction, {
      onSubmit: (message: string) => console.log("Message sent:", message),
      onHelpRequested: () => console.log("Help requested"),
    }),
  },
  {
    id: "derived-coaching-prompts",
    name: "Coaching Prompts",
    category: "derived",
    group: "coaching",
    description:
      "Speech bubble prompt selector with avatar and progress indicator",
    ...demo(CoachingPrompts, {
      userName: "Olivia",
      onPromptClick: (prompt: string) => console.log("Prompt clicked:", prompt),
      currentStep: 1,
      totalSteps: 5,
      question: "What would you like help with?",
    }),
  },

  // ===== APPLICATION =====
  {
    id: "messaging-message-item",
    name: "Message Item",
    category: "application",
    group: "messaging",
    description: "A single message item with avatar and timestamp",
    ...demo(MessageItem, {
      msg: {
        id: "message-001",
        sentAt: "Friday 2:20pm",
        text: "Hey there! This is a demo message showing the MessageItem component.",
        user: {
          name: "Phoenix Baker",
          avatarUrl:
            "https://www.untitledui.com/images/avatars/phoenix-baker?fm=webp&q=80",
          status: "online",
        },
      },
    }),
  },
  {
    id: "messaging-message-item-reply",
    name: "Message Item (with Reply)",
    category: "application",
    group: "messaging",
    description: "Message item showing a reply to a previous message",
    ...demo(MessageItem, {
      msg: {
        id: "message-002",
        sentAt: "Friday 2:20pm",
        text: "Awesome, thanks!",
        user: {
          name: "Phoenix Baker",
          avatarUrl:
            "https://www.untitledui.com/images/avatars/phoenix-baker?fm=webp&q=80",
          status: "online",
        },
        reply: {
          text: "Sure thing, I'll have a look today.",
        },
      },
    }),
  },
  {
    id: "messaging-message-item-attachment",
    name: "Message Item (with Attachment)",
    category: "application",
    group: "messaging",
    description: "Message item with a file attachment",
    ...demo(MessageItem, {
      msg: {
        id: "message-004",
        sentAt: "Friday 2:20pm",
        user: {
          name: "Phoenix Baker",
          avatarUrl:
            "https://www.untitledui.com/images/avatars/phoenix-baker?fm=webp&q=80",
          status: "online",
        },
        attachment: {
          type: "jpg",
          name: "Latest design screenshot.jpg",
          size: "1.2 MB",
        },
      },
    }),
  },
  {
    id: "messaging-message-status-sent",
    name: "Message Status (Sent)",
    category: "application",
    group: "messaging",
    description: "Message status indicator showing sent state",
    ...demo(MessageStatus, {
      status: "sent",
    }),
  },
  {
    id: "messaging-message-status-read",
    name: "Message Status (Read)",
    category: "application",
    group: "messaging",
    description: "Message status indicator showing read state with timestamp",
    ...demo(MessageStatus, {
      status: "read",
      readAt: new Date().toLocaleString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }),
    }),
  },
  {
    id: "messaging-message-status-failed",
    name: "Message Status (Failed)",
    category: "application",
    group: "messaging",
    description: "Message status indicator showing failed state",
    ...demo(MessageStatus, {
      status: "failed",
    }),
  },
  {
    id: "messaging-action-minimal",
    name: "Message Action Minimal",
    category: "application",
    group: "messaging",
    description: "Minimal message input form with send button",
    ...demo(MessageActionMinimal, {
      onSubmit: (message: string) => console.log("Message sent:", message),
    }),
  },
  {
    id: "messaging-action-textarea",
    name: "Message Action Textarea",
    category: "application",
    group: "messaging",
    description: "Message input with textarea and utility buttons",
    ...demo(MessageActionTextarea, {
      onSubmit: (message: string) => console.log("Message sent:", message),
    }),
  },
  {
    id: "messaging-action-advanced",
    name: "Message Action Advanced",
    category: "application",
    group: "messaging",
    description: "Advanced message input with avatar selector and shortcuts",
    ...demo(MessageActionAdvanced, {
      onSubmit: (message: string) => console.log("Message sent:", message),
    }),
  },

  // ===== MODALS =====
  {
    id: "modals-ai-assistant-modal",
    name: "AI Assistant Modal",
    category: "application",
    group: "modals",
    description:
      "Full-featured AI assistant modal with prompt suggestions and message input",
    ...demo(AIAssistantModalPlayground, {}),
  },
]

// === Helper Functions ===

/**
 * Get all unique categories from the registry
 */
export function getCategories(): ComponentCategory[] {
  return Array.from(new Set(componentRegistry.map((c) => c.category)))
}

/**
 * Get all components in a specific category
 */
export function getComponentsByCategory(
  category: ComponentCategory,
): ComponentRegistryEntry[] {
  return componentRegistry.filter((c) => c.category === category)
}

/**
 * Get all unique groups within a category
 */
export function getGroupsByCategory(category: ComponentCategory): string[] {
  const components = getComponentsByCategory(category)
  return Array.from(new Set(components.map((c) => c.group)))
}

/**
 * Get all components in a specific category and group
 */
export function getComponentsByCategoryAndGroup(
  category: ComponentCategory,
  group: string,
): ComponentRegistryEntry[] {
  return componentRegistry.filter(
    (c) => c.category === category && c.group === group,
  )
}

/**
 * Get a single component by ID
 */
export function getComponentById(
  id: string,
): ComponentRegistryEntry | undefined {
  return componentRegistry.find((c) => c.id === id)
}
